import type { ResponsiveImage } from "@/components/site/images";
import {
  LECANTO_METAL_BEFORE,
  LECANTO_METAL_AFTER,
  HERNANDO_GALVALUME_FINISHED,
  HOMOSASSA_BLUE_METAL_IN_PROGRESS,
  CRYSTAL_RIVER_BLUE_SHINGLE,
  HERNANDO_GALVALUME_AERIAL,
  LECANTO_STANDING_SEAM_GALVALUME,
  BEVERLY_HILLS_SHINGLE_TEAR_OFF,
  BEVERLY_HILLS_SHINGLE_UNDERLAYMENT,
  BEVERLY_HILLS_SHINGLE_IN_PROGRESS,
} from "@/components/site/project-images";
import { TAN_STANDING_SEAM_PINE_RIDGE } from "@/components/site/images";

export type CaseStudyPhoto = {
  image: ResponsiveImage;
  alt: string;
  caption: string;
  label?: string;
};

export type CaseStudyFact = { label: string; value: string };

export type CaseStudyLink = { to: string; label: string; description?: string };

export type CaseStudyFaq = { q: string; a: string };

export type ProjectCaseStudy = {
  /** Route slug, without the /projects prefix. */
  slug: string;
  title: string;
  metaDescription: string;
  h1: string;
  breadcrumbLabel: string;
  city: string;
  state: string;
  projectType: string;
  roofType: string;
  /** Free-text status, e.g. "Completed" or "In progress". */
  projectStatus: string;
  /** Set to "in-progress" so UI can badge unfinished projects (e.g. Beverly Hills). */
  statusState?: "completed" | "in-progress";
  /** Confirmed material and color, once the owner supplies them. */
  materialAndColor?: string;
  /** Final scope of work, once confirmed. */
  finalScope?: string;

  /** Short intro paragraph shown under the H1. */
  summary: string;
  /** Compact hero trust row. */
  trustPoints: string[];
  /** Only confirmed facts. Never invent values. */
  facts: CaseStudyFact[];
  before?: CaseStudyPhoto[];
  during?: CaseStudyPhoto[];
  after?: CaseStudyPhoto[];
  /** Owner commentary, written uniquely per project. */
  ownerCommentary?: string;
  relatedServices: CaseStudyLink[];
  /** Only set when the city page already exists. */
  relatedCityPage?: CaseStudyLink;
  faqs: CaseStudyFaq[];
  /** Cards for other real projects; may link to the gallery. */
  relatedProjects: {
    image: ResponsiveImage;
    alt: string;
    title: string;
    note?: string;
    to: string;
  }[];
  /** Contact-form preselection + tracking name. */
  formService: string;
  formName: string;
  formHeading: string;
  formCopy: string;
  finalCta: { heading: string; copy: string };
  /** Extra body sections for generated project pages. */
  sections?: { h2: string; p: string[]; bullets?: string[] }[];
  heroCta?: string;
  serviceLink?: { to: string; label: string };
  /** Social preview image. */
  ogImage: string;
};

export const LECANTO_METAL_CASE_STUDY: ProjectCaseStudy = {
  slug: "lecanto-metal-roof-before-after",
  title: "Lecanto Metal Roof Before and After | Destination Roofing",
  metaDescription:
    "See a real Lecanto metal roof before-and-after project completed by Destination Roofing Inc. View the roof condition, completed work, and metal-roofing options.",
  h1: "Metal Roof Replacement in Lecanto, Florida",
  breadcrumbLabel: "Lecanto Metal Roof Before and After",
  city: "Lecanto",
  state: "Florida",
  projectType: "Replacement roofing work",
  roofType: "Metal roofing",
  projectStatus: "Completed",
  summary:
    "This Lecanto project shows an existing deteriorated metal roofing section before work and the completed replacement section afterward. Destination Roofing Inc. uses real project photographs to help homeowners understand the conditions we inspect and the work we perform across Citrus County.",
  trustPoints: [
    "Real Lecanto roofing project",
    "Owner-led inspections",
    "Free roof inspections",
    "Licensed and insured",
    "License # CCC1335406",
  ],
  facts: [
    { label: "Location", value: "Lecanto, Florida" },
    { label: "Roof type", value: "Metal roofing" },
    { label: "Project type", value: "Replacement roofing work" },
    { label: "Project status", value: "Completed" },
    { label: "Contractor", value: "Destination Roofing Inc." },
    { label: "License", value: "CCC1335406" },
  ],
  before: [
    {
      image: LECANTO_METAL_BEFORE,
      label: "Before",
      alt: "Existing deteriorated metal roof before replacement in Lecanto, Florida",
      caption:
        "Existing deteriorated metal roofing before replacement work in Lecanto, Florida.",
    },
  ],
  after: [
    {
      image: LECANTO_METAL_AFTER,
      label: "After",
      alt: "Completed replacement metal roofing section by Destination Roofing in Lecanto, Florida",
      caption:
        "Completed replacement metal roofing section after Destination Roofing's work in Lecanto, Florida.",
    },
  ],
  ownerCommentary:
    "Photographs are useful for showing conditions, but they never replace an inspection. On an aging metal roof, the recommendation depends on how the panels, fasteners, sealants, flashing, and deck are performing together — not on any single picture.",
  relatedServices: [
    { to: "/metal-roofing", label: "metal roofing in Citrus County", description: "Exposed-fastener and standing-seam metal systems, repairs, and complete re-screws." },
    { to: "/roof-repair", label: "roof repair and leak diagnosis", description: "Isolated leaks, failed fasteners, sealant, trim, and penetration repairs." },
    { to: "/roof-replacement", label: "full roof replacement", description: "Tear-off, deck evaluation, underlayment, and complete new roof installation." },
    { to: "/roof-inspection", label: "free roof inspection", description: "Owner-performed inspection with an explanation of the reasonable options." },
    { to: "/flat-roofing", label: "flat and low-slope roofing", description: "Lanais, additions, and other low-slope areas that need a different system." },
    { to: "/storm-damage-roof-repair", label: "storm-damage roof repair", description: "Wind and storm-related roof evaluation and repair work." },
    { to: "/gallery", label: "roofing project gallery", description: "More real Destination Roofing project photographs from Citrus County." },
  ],
  relatedCityPage: {
    to: "/roofing-company-lecanto-fl",
    label: "roofing company in Lecanto, FL",
  },
  faqs: [
    {
      q: "Can a leaking metal roof be repaired instead of replaced?",
      a: "Yes, when the problem is isolated and the surrounding panels remain structurally serviceable. Failed fasteners, washers, sealant, trim, and penetrations may be repairable.",
    },
    {
      q: "What is a metal-roof re-screw?",
      a: "A complete re-screw involves removing the existing fasteners and installing wider-diameter long-life screws that can properly engage the existing holes. Penetrations and vulnerable details are resealed as needed.",
    },
    {
      q: "When should a metal roof be replaced?",
      a: "Replacement becomes more likely when panels are widely rusted, severely deteriorated, unable to hold replacement fasteners, or leaking across multiple areas.",
    },
    {
      q: "Does Destination Roofing provide free inspections in Lecanto?",
      a: "Yes. Destination Roofing provides free roof inspections for homeowners in Lecanto and throughout its service area.",
    },
    {
      q: "Who performs the roof inspection?",
      a: "Owner and licensed roofing contractor Deston Judy generally performs metal-roof inspections personally when weather, pitch, and safety conditions allow.",
    },
    {
      q: "What is installed under a new metal roof?",
      a: "Destination Roofing installs high-temperature peel-and-stick underlayment under metal roofing. The exact product depends on compatibility, availability, and project requirements.",
    },
    {
      q: "Does metal roofing require maintenance?",
      a: "Yes. Exposed-fastener roofs should be inspected over time for backing-out screws, deteriorated washers, failed sealant, damaged trim, and penetration leaks.",
    },
    {
      q: "What workmanship warranty comes with a full metal roof?",
      a: "Standard full-roof installations generally include a 15-year Destination Roofing workmanship warranty. Exact coverage is controlled by the written contract and warranty.",
    },
  ],
  relatedProjects: [
    {
      image: HERNANDO_GALVALUME_FINISHED,
      alt: "Completed Galvalume metal roof installed by Destination Roofing Inc. in Hernando, Florida",
      title: "Galvalume metal roof — Hernando, FL",
      to: "/projects/galvalume-metal-roof-hernando-fl",
    },
    {
      image: HOMOSASSA_BLUE_METAL_IN_PROGRESS,
      alt: "Destination Roofing crew installing a blue metal roof in Homosassa, Florida",
      title: "Blue metal roof — Homosassa, FL",
      note: "Installation in Progress",
      to: "/projects/blue-metal-roof-homosassa-fl",
    },
    {
      image: CRYSTAL_RIVER_BLUE_SHINGLE,
      alt: "Completed blue architectural shingle roof by Destination Roofing Inc. in Crystal River, Florida",
      title: "Blue shingle roof — Crystal River, FL",
      to: "/projects/blue-shingle-roof-crystal-river-fl",
    },
  ],
  formService: "Metal Roofing",
  formName: "project_lecanto_metal_roof_before_after",
  formHeading: "Request a Free Metal-Roof Inspection in Lecanto",
  formCopy:
    "Tell us what you have noticed about the roof and whether you are considering repair, re-screwing, or replacement. Destination Roofing will contact you to schedule a free inspection.",
  finalCta: {
    heading: "Get an Honest Metal-Roof Recommendation",
    copy: "Destination Roofing will inspect the actual conditions and explain whether repair, re-screwing, or replacement makes the most sense for the roof.",
  },
  ogImage: LECANTO_METAL_AFTER.fallback,
};


/* ------------------------------------------------------------------ */
/* Local proof project pages (verified facts only).                    */
/* ------------------------------------------------------------------ */

const TRUST = [
  "Real Destination Roofing project",
  "Owner-led inspections",
  "Free roof inspections",
  "Licensed and insured",
  "License # CCC1335406",
];

const METAL_UNDERLAYMENT_FAQ: CaseStudyFaq = {
  q: "What does Destination Roofing install under a new metal roof?",
  a: "Destination Roofing installs high-temperature peel-and-stick underlayment under metal roofing. The exact product depends on compatibility, availability, and project requirements.",
};
const WARRANTY_FAQ: CaseStudyFaq = {
  q: "What workmanship warranty comes with a full roof?",
  a: "Standard full-roof installations generally include a 15-year Destination Roofing workmanship warranty, and most repairs include a five-year workmanship warranty. Exact coverage is controlled by the written contract and warranty.",
};
const INSPECTION_FAQ = (city: string): CaseStudyFaq => ({
  q: `Does Destination Roofing offer free roof inspections in ${city}?`,
  a: `Yes. ${city} is part of Destination Roofing's normal service area. Owner and licensed roofing contractor Deston Judy generally performs inspections personally. Call (352) 901-3094 or use the form on this page.`,
});

export const HERNANDO_GALVALUME_STUDY: ProjectCaseStudy = {
  slug: "galvalume-metal-roof-hernando-fl",
  title: "Galvalume Metal Roof in Hernando, FL | Destination Roofing",
  metaDescription:
    "A completed Galvalume metal roof on a residential home in Hernando, FL by Destination Roofing Inc. See real project photos and how we approach metal roofing.",
  h1: "Galvalume Metal Roof in Hernando, FL",
  breadcrumbLabel: "Galvalume Metal Roof in Hernando, FL",
  city: "Hernando",
  state: "Florida",
  projectType: "Metal roof installation",
  roofType: "Metal roofing",
  projectStatus: "Completed",
  statusState: "completed",
  summary:
    "A completed Galvalume metal roofing system installed by Destination Roofing Inc. on a residential home in Hernando, Florida. These are real photographs from the finished job.",
  trustPoints: TRUST,
  facts: [
    { label: "Location", value: "Hernando, Florida" },
    { label: "Building", value: "Residential home" },
    { label: "Roof type", value: "Galvalume metal roofing" },
    { label: "Project status", value: "Completed" },
    { label: "Contractor", value: "Destination Roofing Inc." },
    { label: "License", value: "CCC1335406" },
  ],
  after: [
    {
      image: HERNANDO_GALVALUME_FINISHED,
      label: "Completed",
      alt: "Completed Galvalume metal roof installed by Destination Roofing on a residential home in Hernando, Florida",
      caption: "Completed Galvalume metal roof on a residential home in Hernando, Florida.",
    },
    {
      image: HERNANDO_GALVALUME_AERIAL,
      label: "Aerial",
      alt: "Aerial view of the completed Galvalume metal roof by Destination Roofing in Hernando, Florida",
      caption: "Aerial view of the same completed Galvalume metal roof in Hernando.",
    },
  ],
  sections: [
    {
      h2: "What the Photographs Show",
      p: [
        "The ground-level photograph and the aerial view both show the finished Galvalume metal roof on this Hernando home. Galvalume is steel coated with an aluminum-zinc alloy, which gives the roof its bare metallic finish instead of a painted color.",
        "The aerial view is useful because it shows the whole roof at once — the panel runs, ridges, and edges working together as one system rather than a single section in isolation.",
      ],
    },
    {
      h2: "Why Homeowners Choose Galvalume Metal",
      p: [
        "Galvalume is a common choice for homeowners who want a metal roof with a clean, unpainted appearance. Whether a Galvalume system is the right fit depends on the home's structure, roof slope, budget, and long-term plans — which is why Destination Roofing starts every project with an inspection instead of a sales pitch.",
        "Metal roofing can be installed as an exposed-fastener system or as a concealed-fastener standing-seam system. Each has different cost, appearance, and maintenance considerations, and we explain both before a homeowner decides.",
      ],
    },
    {
      h2: "Installation Details That Matter on a Metal Roof",
      p: [
        "A metal roof performs only as well as the details beneath and around the panels. On metal roofs Destination Roofing installs high-temperature peel-and-stick underlayment, integrates new metal trim and flashing at eaves, gables, ridges, and walls, and uses manufacturer-compatible sealants.",
      ],
      bullets: [
        "Correct screw placement and washer compression on exposed-fastener systems",
        "Fasteners matched to wood-to-metal or metal-to-metal connections",
        "Compatible boots and flashing at pipes, vents, and other penetrations",
        "Drainage detailing so water is not trapped behind flashing or transitions",
      ],
    },
  ],
  relatedServices: [
    { to: "/metal-roofing", label: "Metal roofing", description: "Exposed-fastener and standing-seam metal systems, repairs, and re-screws." },
    { to: "/roof-replacement", label: "Roof replacement", description: "Tear-off, deck evaluation, underlayment, and complete new roof installation." },
    { to: "/roof-inspection", label: "Free roof inspection", description: "Owner-performed inspection with honest options." },
  ],
  relatedCityPage: { to: "/roofing-company-hernando-fl", label: "Roofing company in Hernando, FL" },
  faqs: [
    INSPECTION_FAQ("Hernando"),
    {
      q: "Is Galvalume the only metal roof option?",
      a: "No. Destination Roofing installs both exposed-fastener and standing-seam metal systems in several finishes. The right choice depends on the home, budget, and goals.",
    },
    METAL_UNDERLAYMENT_FAQ,
    WARRANTY_FAQ,
  ],
  relatedProjects: [],
  formService: "Metal Roofing",
  formName: "project_hernando_galvalume_metal_roof",
  formHeading: "Request a Free Roof Inspection in Hernando",
  formCopy: "Tell us about your roof and whether you are considering metal. Destination Roofing will contact you to schedule a free inspection.",
  finalCta: {
    heading: "Considering a Metal Roof in Hernando?",
    copy: "Destination Roofing will inspect your current roof and explain whether a metal system makes sense for your home and budget.",
  },
  heroCta: "Request a Free Roof Inspection",
  serviceLink: { to: "/metal-roofing", label: "metal roofing" },
  ogImage: HERNANDO_GALVALUME_FINISHED.fallback,
};

export const CRYSTAL_RIVER_SHINGLE_STUDY: ProjectCaseStudy = {
  slug: "blue-shingle-roof-crystal-river-fl",
  title: "Blue Shingle Roof in Crystal River, FL | Destination Roofing",
  metaDescription:
    "A completed blue architectural shingle roof in Crystal River, FL by Destination Roofing Inc. View the real project photo and learn what goes into a shingle roof.",
  h1: "Blue Architectural Shingle Roof in Crystal River, FL",
  breadcrumbLabel: "Blue Shingle Roof in Crystal River, FL",
  city: "Crystal River",
  state: "Florida",
  projectType: "Shingle roofing",
  roofType: "Architectural shingle roofing",
  projectStatus: "Completed",
  statusState: "completed",
  summary:
    "A completed blue architectural shingle roof by Destination Roofing Inc. in Crystal River, Florida — a real finished project from our Citrus County service area.",
  trustPoints: TRUST,
  facts: [
    { label: "Location", value: "Crystal River, Florida" },
    { label: "Roof type", value: "Architectural shingles" },
    { label: "Color", value: "Blue" },
    { label: "Project status", value: "Completed" },
    { label: "Contractor", value: "Destination Roofing Inc." },
    { label: "License", value: "CCC1335406" },
  ],
  after: [
    {
      image: CRYSTAL_RIVER_BLUE_SHINGLE,
      label: "Completed",
      alt: "Blue architectural shingle roof completed by Destination Roofing in Crystal River, Florida",
      caption: "Completed blue architectural shingle roof in Crystal River, Florida.",
    },
  ],
  sections: [
    {
      h2: "What the Photograph Shows",
      p: [
        "The photograph shows the finished blue architectural shingle roof on this Crystal River home. Architectural (dimensional) shingles have a layered profile that gives the roof more depth and texture than a flat three-tab shingle.",
        "Color is one of the few roofing decisions a homeowner sees every day. A blue shingle like this one is a deliberate choice, and we encourage homeowners to look at real installed roofs — not just small samples — before committing.",
      ],
    },
    {
      h2: "Why Architectural Shingles Are a Common Choice",
      p: [
        "Architectural shingles are the most common residential roof we install in Citrus County. Destination Roofing primarily installs Atlas shingle systems, and owner Deston Judy is an Atlas Certified Scotchgard™ Shingle Sales Specialist.",
        "Shingles are usually a lower up-front investment than metal, and they suit many roof designs. Whether shingles or metal make more sense depends on the home's structure, roof slope, budget, and how long the owner plans to stay.",
      ],
    },
    {
      h2: "What Matters During a Shingle Installation",
      p: [
        "A shingle roof is a system, not just the visible layer. The long-term performance depends on the work underneath and around the shingles.",
      ],
      bullets: [
        "Removal of the old roofing and evaluation of the roof deck",
        "Underlayment installed before the new shingles",
        "Correct fastening pattern per the manufacturer's requirements",
        "Flashing at walls, valleys, and penetrations",
        "Ventilation reviewed as part of the roof system",
      ],
    },
  ],
  relatedServices: [
    { to: "/shingle-roofing", label: "Shingle roofing", description: "Architectural shingle installation with Atlas shingle systems." },
    { to: "/roof-replacement", label: "Roof replacement", description: "Complete tear-off and new roof installation." },
    { to: "/atlas-shingle-colors", label: "Atlas shingle colors", description: "Compare shingle colors before choosing." },
  ],
  relatedCityPage: { to: "/roofing-company-crystal-river-fl", label: "Roofing company in Crystal River, FL" },
  faqs: [
    INSPECTION_FAQ("Crystal River"),
    {
      q: "What shingles does Destination Roofing install?",
      a: "Destination Roofing primarily installs Atlas shingle systems. The specific line and color are chosen with the homeowner during the estimate.",
    },
    {
      q: "Should I choose shingles or metal?",
      a: "It depends on the home's structure, roof slope, budget, and long-term goals. We explain both options honestly after inspecting the roof.",
    },
    WARRANTY_FAQ,
  ],
  relatedProjects: [],
  formService: "Shingle Roofing",
  formName: "project_crystal_river_blue_shingle",
  formHeading: "Request a Free Roof Inspection in Crystal River",
  formCopy: "Tell us about your roof and what you are considering. Destination Roofing will contact you to schedule a free inspection.",
  finalCta: {
    heading: "Planning a New Shingle Roof in Crystal River?",
    copy: "Destination Roofing will inspect your roof and explain your shingle options, colors, and a written estimate.",
  },
  heroCta: "Request a Free Roof Inspection",
  serviceLink: { to: "/shingle-roofing", label: "shingle roofing" },
  ogImage: CRYSTAL_RIVER_BLUE_SHINGLE.fallback,
};

export const HOMOSASSA_BLUE_METAL_STUDY: ProjectCaseStudy = {
  slug: "blue-metal-roof-homosassa-fl",
  title: "Blue Metal Roof Installation in Homosassa, FL | Destination Roofing",
  metaDescription:
    "Real in-progress photos of a Destination Roofing crew installing a blue metal roof on a home addition in Homosassa, FL. See what goes into a metal roof install.",
  h1: "Blue Metal Roof Installation in Homosassa, FL",
  breadcrumbLabel: "Blue Metal Roof in Homosassa, FL",
  city: "Homosassa",
  state: "Florida",
  projectType: "Metal roof installation",
  roofType: "Metal roofing",
  projectStatus: "Installation in progress",
  statusState: "in-progress",
  summary:
    "A Destination Roofing crew installing a blue metal roofing system on a home addition in Homosassa, Florida. This photograph was taken while the installation was in progress.",
  trustPoints: TRUST,
  facts: [
    { label: "Location", value: "Homosassa, Florida" },
    { label: "Area", value: "Home addition" },
    { label: "Roof type", value: "Metal roofing" },
    { label: "Color", value: "Blue" },
    { label: "Photo stage", value: "Installation in progress" },
    { label: "Contractor", value: "Destination Roofing Inc." },
  ],
  during: [
    {
      image: HOMOSASSA_BLUE_METAL_IN_PROGRESS,
      label: "In Progress",
      alt: "Destination Roofing crew installing a blue metal roof on a home addition in Homosassa, Florida",
      caption: "Crew installing a blue metal roofing system in Homosassa, Florida. Installation in progress.",
    },
  ],
  sections: [
    {
      h2: "What the Photograph Shows",
      p: [
        "This photograph captures the Destination Roofing crew on the roof during installation — the work is not finished in this image. It shows blue metal panels going onto a home addition in Homosassa.",
        "In-progress photos are valuable because they show what homeowners rarely see: the crew, the materials, and the stage-by-stage work that happens before a roof looks finished from the street.",
      ],
    },
    {
      h2: "Metal Roofing on Additions",
      p: [
        "Additions often meet the main house at walls and transitions, and those junctions are where many leaks begin. Whether an addition gets metal, shingles, or a low-slope system depends on its pitch, how it ties into the existing roof, and the homeowner's goals.",
        "When pitch is too low for metal or shingles, a flat or low-slope system may be the right answer instead — something we evaluate during the inspection.",
      ],
    },
    {
      h2: "Installation Details That Matter",
      p: [
        "Destination Roofing installs high-temperature peel-and-stick underlayment under metal roofing and integrates new trim and flashing with the panel system.",
      ],
      bullets: [
        "Wall and transition flashing where the addition meets the house",
        "Correct screw placement and washer compression on exposed-fastener panels",
        "Manufacturer-compatible, color-matched sealants",
        "Compatible boots at pipes and other penetrations",
      ],
    },
  ],
  relatedServices: [
    { to: "/metal-roofing", label: "Metal roofing", description: "Exposed-fastener and standing-seam systems, repairs, and re-screws." },
    { to: "/flat-roofing", label: "Flat and low-slope roofing", description: "For additions and lanais with too little pitch for metal or shingles." },
    { to: "/roof-inspection", label: "Free roof inspection", description: "Owner-performed inspection with honest options." },
  ],
  relatedCityPage: { to: "/roofing-company-homosassa-fl", label: "Roofing company in Homosassa, FL" },
  faqs: [
    INSPECTION_FAQ("Homosassa"),
    {
      q: "Can you put a metal roof on an addition?",
      a: "Often, yes — if the pitch is suitable. Very low-slope additions may need a flat or low-slope roofing system instead.",
    },
    METAL_UNDERLAYMENT_FAQ,
    WARRANTY_FAQ,
  ],
  relatedProjects: [],
  formService: "Metal Roofing",
  formName: "project_homosassa_blue_metal_roof",
  formHeading: "Request a Free Roof Inspection in Homosassa",
  formCopy: "Tell us about your roof or addition. Destination Roofing will contact you to schedule a free inspection.",
  finalCta: {
    heading: "Thinking About Metal Roofing in Homosassa?",
    copy: "Destination Roofing will inspect the roof and explain whether metal, shingles, or a low-slope system fits best.",
  },
  heroCta: "Request a Free Roof Inspection",
  serviceLink: { to: "/metal-roofing", label: "metal roofing" },
  ogImage: HOMOSASSA_BLUE_METAL_IN_PROGRESS.fallback,
};

export const LECANTO_STANDING_SEAM_STUDY: ProjectCaseStudy = {
  slug: "standing-seam-metal-roof-lecanto-fl",
  title: "Standing Seam Metal Roof in Lecanto, FL | Destination Roofing",
  metaDescription:
    "26-gauge Galvalume standing-seam metal roofing installed by Destination Roofing Inc. in Lecanto, FL. See the real project photo and how standing seam works.",
  h1: "26-Gauge Galvalume Standing-Seam Roof in Lecanto, FL",
  breadcrumbLabel: "Standing-Seam Metal Roof in Lecanto, FL",
  city: "Lecanto",
  state: "Florida",
  projectType: "Standing-seam metal roof installation",
  roofType: "Standing-seam metal roofing",
  projectStatus: "Completed",
  statusState: "completed",
  summary:
    "26-gauge Galvalume standing-seam metal roofing installed by Destination Roofing Inc. in Lecanto, Citrus County, Florida.",
  trustPoints: TRUST,
  facts: [
    { label: "Location", value: "Lecanto, Citrus County, Florida" },
    { label: "Roof type", value: "Standing-seam metal" },
    { label: "Material", value: "26-gauge Galvalume" },
    { label: "Fasteners", value: "Concealed beneath the seams" },
    { label: "Contractor", value: "Destination Roofing Inc." },
    { label: "License", value: "CCC1335406" },
  ],
  after: [
    {
      image: LECANTO_STANDING_SEAM_GALVALUME,
      label: "Completed",
      alt: "26-gauge Galvalume standing-seam metal roof installed by Destination Roofing in Lecanto, Florida",
      caption: "26-gauge Galvalume standing-seam metal roofing in Lecanto, Citrus County, Florida.",
    },
  ],
  sections: [
    {
      h2: "What the Photograph Shows",
      p: [
        "The photograph shows 26-gauge Galvalume standing-seam panels on this Lecanto roof. The raised vertical seams are the defining feature of the system — the primary panel fasteners sit hidden beneath those seams instead of being exposed to the weather.",
        "The result is a clean, uninterrupted finished appearance with no rows of visible screw heads across the panels.",
      ],
    },
    {
      h2: "How Standing Seam Differs From Exposed-Fastener Metal",
      p: [
        "Exposed-fastener metal panels are screwed directly through the face of the panel. They cost less up front, but the screws and washers need to be monitored over time for backing out and washer deterioration.",
        "Standing seam is a premium concealed-fastener system. Destination Roofing's most common standing-seam configuration is snap lock with a concealed screw strip, depending on the roof design and manufacturer requirements. Other options include snap-lock panels with clips and mechanically seamed panels.",
        "Gauge refers to the thickness of the steel — this project used 26-gauge. For standing-seam residential systems, Destination Roofing generally recommends approximately 24-gauge material depending on engineering, manufacturer, and project requirements, and we discuss gauge options with each homeowner.",
      ],
    },
    {
      h2: "Installation Details That Matter",
      p: [
        "Under metal roofing, Destination Roofing installs high-temperature peel-and-stick underlayment. Trim and flashing are installed as new components integrated with the panel system at eaves, gables, ridges, valleys, and walls.",
      ],
    },
  ],
  relatedServices: [
    { to: "/metal-roofing", label: "Metal roofing", description: "Standing-seam and exposed-fastener systems explained." },
    { to: "/roof-replacement", label: "Roof replacement", description: "Complete tear-off and new roof installation." },
    { to: "/projects/lecanto-metal-roof-before-after", label: "Lecanto metal roof before and after", description: "Another real Lecanto metal roofing project." },
  ],
  relatedCityPage: { to: "/roofing-company-lecanto-fl", label: "Roofing company in Lecanto, FL" },
  faqs: [
    INSPECTION_FAQ("Lecanto"),
    {
      q: "Is standing seam better than exposed-fastener metal?",
      a: "Standing seam hides the primary fasteners and has a cleaner look, but costs more. Exposed-fastener metal is more economical. The right choice depends on budget and goals.",
    },
    METAL_UNDERLAYMENT_FAQ,
    WARRANTY_FAQ,
  ],
  relatedProjects: [],
  formService: "Metal Roofing",
  formName: "project_lecanto_standing_seam",
  formHeading: "Request a Free Roof Inspection in Lecanto",
  formCopy: "Tell us about your roof and whether you are considering standing seam. Destination Roofing will contact you to schedule a free inspection.",
  finalCta: {
    heading: "Considering Standing Seam in Lecanto?",
    copy: "Destination Roofing will inspect your roof and explain standing-seam and exposed-fastener options with a written estimate.",
  },
  heroCta: "Request a Free Roof Inspection",
  serviceLink: { to: "/metal-roofing", label: "standing-seam metal roofing" },
  ogImage: LECANTO_STANDING_SEAM_GALVALUME.fallback,
};

export const BEVERLY_HILLS_SHINGLE_STUDY: ProjectCaseStudy = {
  slug: "shingle-roof-replacement-beverly-hills-fl",
  title: "Shingle Roof Replacement in Beverly Hills, FL | Destination Roofing",
  metaDescription:
    "Real in-progress photos of a residential shingle roof replacement in Beverly Hills, FL by Destination Roofing Inc. — tear-off, underlayment and installation prep.",
  h1: "Shingle Roof Replacement in Beverly Hills, FL",
  breadcrumbLabel: "Shingle Roof Replacement in Beverly Hills, FL",
  city: "Beverly Hills",
  state: "Florida",
  projectType: "Residential shingle roof replacement",
  roofType: "Shingle roofing",
  projectStatus: "In progress",
  statusState: "in-progress",
  summary:
    "A residential shingle roof replacement by Destination Roofing Inc. in Beverly Hills, Florida. These photographs show the work in progress — tear-off, underlayment, and installation preparation.",
  trustPoints: TRUST,
  facts: [
    { label: "Location", value: "Beverly Hills, Florida" },
    { label: "Building", value: "Residential home" },
    { label: "Roof type", value: "Shingle roofing" },
    { label: "Project type", value: "Roof replacement" },
    { label: "Photo stage", value: "In progress" },
    { label: "Contractor", value: "Destination Roofing Inc." },
  ],
  during: [
    {
      image: BEVERLY_HILLS_SHINGLE_TEAR_OFF,
      label: "Tear-Off",
      alt: "Destination Roofing crew tearing off an existing shingle roof in Beverly Hills, Florida",
      caption: "Crew removing the existing shingle roofing from multiple roof sections.",
    },
    {
      image: BEVERLY_HILLS_SHINGLE_UNDERLAYMENT,
      label: "Underlayment",
      alt: "Synthetic roof underlayment installed during a shingle replacement in Beverly Hills, Florida",
      caption: "Synthetic underlayment installed before the new shingles are applied.",
    },
    {
      image: BEVERLY_HILLS_SHINGLE_IN_PROGRESS,
      label: "Installation Prep",
      alt: "Residential shingle roof replacement in progress in Beverly Hills, Florida",
      caption: "Roofing materials staged while the replacement continues.",
    },
  ],
  sections: [
    {
      h2: "What the Photographs Show",
      p: [
        "These three photographs follow the replacement in stages. First, the crew removes the existing shingle roofing from multiple roof sections. Next, synthetic underlayment is installed over the deck before any new shingles go on. Finally, roofing materials are staged while the installation continues.",
        "The roof is not finished in these images. We show in-progress work because it is the part of a roof replacement homeowners rarely get to see — and the part that matters most for long-term performance.",
      ],
    },
    {
      h2: "Why Tear-Off Matters",
      p: [
        "Removing the old roofing exposes the roof deck so it can be evaluated. Soft, damaged, or deteriorated decking can only be found and addressed once the old material is off. Installing new shingles over a problem deck hides the problem rather than fixing it.",
      ],
    },
    {
      h2: "Underlayment and Installation",
      p: [
        "Underlayment is the secondary water barrier beneath the shingles. On this project, synthetic underlayment was installed before the new shingles were applied. After underlayment, the shingles, flashing, and ventilation details complete the roof system.",
      ],
      bullets: [
        "Deck evaluated after tear-off",
        "Synthetic underlayment installed before shingles",
        "Flashing at walls, valleys, and penetrations",
        "Correct shingle fastening per manufacturer requirements",
        "Cleanup and final review by the owner",
      ],
    },
  ],
  relatedServices: [
    { to: "/shingle-roofing", label: "Shingle roofing", description: "Architectural shingle installation with Atlas shingle systems." },
    { to: "/roof-replacement", label: "Roof replacement", description: "Tear-off, deck evaluation, underlayment, and new roof installation." },
    { to: "/roof-inspection", label: "Free roof inspection", description: "Owner-performed inspection with honest options." },
  ],
  relatedCityPage: { to: "/roofing-company-beverly-hills-fl", label: "Roofing company in Beverly Hills, FL" },
  faqs: [
    INSPECTION_FAQ("Beverly Hills"),
    {
      q: "Why remove the old roof instead of roofing over it?",
      a: "Tear-off lets the roof deck be evaluated and repaired where needed before the new roof is installed.",
    },
    {
      q: "What goes under new shingles?",
      a: "Underlayment is installed over the deck before shingles. On this Beverly Hills project, synthetic underlayment was used.",
    },
    WARRANTY_FAQ,
  ],
  relatedProjects: [],
  formService: "Roof Replacement",
  formName: "project_beverly_hills_shingle_replacement",
  formHeading: "Request a Free Roof Inspection in Beverly Hills",
  formCopy: "Tell us about your roof. Destination Roofing will contact you to schedule a free inspection and written estimate.",
  finalCta: {
    heading: "Need a Roof Replacement in Beverly Hills?",
    copy: "Destination Roofing will inspect your roof and explain whether repair or replacement makes the most sense.",
  },
  heroCta: "Request a Free Roof Inspection",
  serviceLink: { to: "/shingle-roofing", label: "shingle roofing" },
  ogImage: BEVERLY_HILLS_SHINGLE_TEAR_OFF.fallback,
};

export const PINE_RIDGE_STANDING_SEAM_STUDY: ProjectCaseStudy = {
  slug: "standing-seam-metal-roof-pine-ridge-fl",
  title: "Standing Seam Metal Roof in Pine Ridge, FL | Destination Roofing",
  metaDescription:
    "A tan standing-seam metal roof installed by Destination Roofing Inc. on a new-construction home in Pine Ridge, FL. See the real project photo and system details.",
  h1: "Standing-Seam Metal Roof in Pine Ridge, FL",
  breadcrumbLabel: "Standing-Seam Metal Roof in Pine Ridge, FL",
  city: "Pine Ridge",
  state: "Florida",
  projectType: "New-construction metal roof",
  roofType: "Standing-seam metal roofing",
  projectStatus: "Completed",
  statusState: "completed",
  summary:
    "A tan standing-seam metal roof installed by Destination Roofing Inc. on a new-construction home in Pine Ridge, Florida.",
  trustPoints: TRUST,
  facts: [
    { label: "Location", value: "Pine Ridge, Florida" },
    { label: "Building", value: "New-construction home" },
    { label: "Roof type", value: "Standing-seam metal" },
    { label: "Color", value: "Tan" },
    { label: "Contractor", value: "Destination Roofing Inc." },
    { label: "License", value: "CCC1335406" },
  ],
  after: [
    {
      image: TAN_STANDING_SEAM_PINE_RIDGE,
      label: "Completed",
      alt: "Tan standing-seam metal roof installed by Destination Roofing on a new-construction home in Pine Ridge, Florida",
      caption: "Tan standing-seam metal roof on a new-construction home in Pine Ridge, Florida.",
    },
  ],
  sections: [
    {
      h2: "What the Photograph Shows",
      p: [
        "The photograph shows a tan standing-seam metal roof on a new-construction home in Pine Ridge. The raised vertical seams run the length of each panel, and the primary fasteners are concealed beneath them rather than exposed on the panel face.",
      ],
    },
    {
      h2: "Standing Seam on New Construction",
      p: [
        "New construction gives the roofing system a clean start: there is no old roof to remove, and the underlayment, trim, and panels can be installed as one planned system from the deck up.",
        "Standing seam is a premium concealed-fastener system. Destination Roofing's most common configuration is snap lock with a concealed screw strip, depending on roof design and manufacturer requirements. We generally recommend approximately 24-gauge material for standing-seam residential systems, depending on engineering, manufacturer, and project requirements.",
      ],
    },
    {
      h2: "Installation Details That Matter",
      p: [
        "Destination Roofing installs high-temperature peel-and-stick underlayment under metal roofing and integrates trim and flashing with the panel system.",
      ],
      bullets: [
        "Panels and trim installed to manufacturer requirements",
        "Flashing integrated at eaves, gables, ridges, valleys, and walls",
        "Compatible boots and flashing at roof penetrations",
        "Drainage detailing so water moves toward the intended path",
      ],
    },
  ],
  relatedServices: [
    { to: "/metal-roofing", label: "Metal roofing", description: "Standing-seam and exposed-fastener systems explained." },
    { to: "/roof-replacement", label: "Roof replacement", description: "Replacing an existing roof with metal or shingles." },
    { to: "/roof-inspection", label: "Free roof inspection", description: "Owner-performed inspection with honest options." },
  ],
  relatedCityPage: { to: "/roofing-company-pine-ridge-fl", label: "Roofing company in Pine Ridge, FL" },
  faqs: [
    INSPECTION_FAQ("Pine Ridge"),
    {
      q: "Do you install standing seam on existing homes too?",
      a: "Yes. Standing seam can be installed on new construction or as part of a roof replacement on an existing home, depending on the roof design.",
    },
    METAL_UNDERLAYMENT_FAQ,
    WARRANTY_FAQ,
  ],
  relatedProjects: [],
  formService: "Metal Roofing",
  formName: "project_pine_ridge_standing_seam",
  formHeading: "Request a Free Roof Inspection in Pine Ridge",
  formCopy: "Tell us about your roof or building project. Destination Roofing will contact you to schedule a free inspection.",
  finalCta: {
    heading: "Considering Standing Seam in Pine Ridge?",
    copy: "Destination Roofing will review your roof or plans and explain standing-seam options with a written estimate.",
  },
  heroCta: "Request a Free Roof Inspection",
  serviceLink: { to: "/metal-roofing", label: "standing-seam metal roofing" },
  ogImage: TAN_STANDING_SEAM_PINE_RIDGE.fallback,
};

/** Only real, published case studies belong here. */
export const CASE_STUDIES: ProjectCaseStudy[] = [
  HERNANDO_GALVALUME_STUDY,
  CRYSTAL_RIVER_SHINGLE_STUDY,
  HOMOSASSA_BLUE_METAL_STUDY,
  LECANTO_METAL_CASE_STUDY,
  LECANTO_STANDING_SEAM_STUDY,
  BEVERLY_HILLS_SHINGLE_STUDY,
  PINE_RIDGE_STANDING_SEAM_STUDY,
];
